import React, { memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Stack } from '@mui/material';
import { parseFormattedContent } from 'app/pages/loanApplication/components/GuidelineCitations/CitedContent/CitedContent.utils';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { AiChatMessageStatus } from 'constants/aiChat';
import { copyToClipboard } from 'utils/copyToClipboard';

import { retryMessageAction } from 'store/aiChat/actions';
import {
  selectAiChatCitationsByMessageId,
  selectAiChatMessageContentBlocksByMessageId,
  selectExecutionPlanByExecutionId,
} from 'store/aiChat/selectors';
import { selectApplicationIdFromDeal } from 'store/loanApplications/selectors/deal.selectors';

import { AiChatTestIds } from '../../AiChat.testIds';
import ActionPlan from '../ActionPlan';
import MessageTools, { MESSAGE_TOOLS_CLASS_NAME } from '../MessageTools';
import PendingQuestionCard from '../PendingQuestionCard';
import ToolConfirmationCard from '../ToolConfirmationCard';

import { MessageContentType } from './BotMessage.constants';
import {
  containerStyles,
  contentWrapperStyles,
  messageContentListStyles,
  messageToolsHiddenStyles,
  streamingCaretStyles,
} from './BotMessage.styles';
import {
  contentTypeCopyFormatterMap,
  filterActionStepsCoveredByPlan,
  groupActionStepBlocks,
  isValidMessageContentItem,
} from './BotMessage.utils';
import FailedBotMessageContent from './components/FailedBotMessageContent';
import FeedbackButtons from './components/FeedbackButtons';
import MessageContent from './components/MessageContent';
import GuidelineExpanderCardContent from './components/MessageContent/GuidelineExpanderCardContent';
import MessageStatus, { MessageStatusType } from './components/MessageStatus';

const BotMessage = ({ message, loanId = null, isShownTools = true }) => {
  const {
    id: messageId,
    content,
    messageContent,
    timestamp,
    messageStatus,
    isStreaming,
    status,
    error,
    canRetry,
    isCancelled,
    relatedUserMessageId,
  } = message ?? {};

  const dispatch = useDispatch();
  const applicationId = useSelector(selectApplicationIdFromDeal);
  const citations = useSelector(state => selectAiChatCitationsByMessageId(state, messageId));
  const streamedContentBlocks = useSelector(state =>
    selectAiChatMessageContentBlocksByMessageId(state, messageId),
  );
  const executionPlan = useSelector(state => selectExecutionPlanByExecutionId(state, messageId));

  // Once any execution plan is attached to the message, the ActionPlan card
  // is the canonical view of every tool execution. Suppress the legacy
  // ACTION_STEP / ACTION_STEP_GROUP rows regardless of whether the plan has
  // already enriched its steps. During streaming the steps array can briefly
  // be empty between `plan_created` and the first `plan_updated` event;
  // a stricter check would let duplicate rows flash through that window.
  const planCoversToolExecutions = !_.isNil(executionPlan);

  const effectiveMessageContent = useMemo(() => {
    const sourceBlocks = !_.isEmpty(messageContent) ? messageContent : streamedContentBlocks;

    if (_.isEmpty(sourceBlocks)) {
      return null;
    }

    return filterActionStepsCoveredByPlan(
      groupActionStepBlocks(sourceBlocks),
      planCoversToolExecutions,
    );
  }, [messageContent, streamedContentBlocks, planCoversToolExecutions]);

  const isFailed = status === AiChatMessageStatus.FAILED;

  const inlineOptions = useMemo(
    () => ({ citations, CitationCard: GuidelineExpanderCardContent }),
    [citations],
  );

  const formattedContent = useMemo(
    () => parseFormattedContent(content, inlineOptions),
    [content, inlineOptions],
  );

  const messageContentCopyText = useMemo(() => {
    if (!effectiveMessageContent) {
      return '';
    }

    return effectiveMessageContent
      .filter(isValidMessageContentItem)
      .map(item => {
        const formatter = contentTypeCopyFormatterMap[item.contentType];

        return formatter ? formatter(item.contentProps) : '';
      })
      .filter(Boolean)
      .join('\n');
  }, [effectiveMessageContent]);

  const copyText = useMemo(
    () => (effectiveMessageContent ? messageContentCopyText : content || ''),
    [content, effectiveMessageContent, messageContentCopyText],
  );

  const onCopy = useCallback(
    value => {
      const textToCopy = _.isString(value) ? value : copyText;

      copyToClipboard(textToCopy);
    },
    [copyText],
  );

  const onCopyAll = useCallback(() => {
    onCopy(copyText);
  }, [copyText, onCopy]);

  const onRetry = useCallback(() => {
    const retryTarget = relatedUserMessageId ?? messageId;

    if (_.isNil(loanId) || _.isNil(retryTarget)) {
      return;
    }

    dispatch(retryMessageAction({ loanId, applicationId, messageId: retryTarget }));
  }, [dispatch, loanId, applicationId, relatedUserMessageId, messageId]);

  return (
    <Box data-testid={AiChatTestIds.AI_BOT_MESSAGE} sx={containerStyles}>
      {isFailed && (
        <Stack data-testid={AiChatTestIds.AI_BOT_MESSAGE_CONTENT_WRAPPER} sx={contentWrapperStyles}>
          <FailedBotMessageContent
            canRetry={canRetry !== false}
            error={error}
            isCancelled={isCancelled === true}
            onRetry={onRetry}
          />
        </Stack>
      )}

      {!isFailed && (
        <Stack data-testid={AiChatTestIds.AI_BOT_MESSAGE_CONTENT_WRAPPER} sx={contentWrapperStyles}>
          {messageStatus && (
            <MessageStatus
              errorsAmount={messageStatus.errorsAmount}
              status={messageStatus.status}
              statusMessage={messageStatus.statusMessage}
            />
          )}
          <ActionPlan
            executionId={messageId}
            hasPersistedPlan={message?.hasExecutionPlan === true}
            loanId={loanId}
          />
          <Stack
            data-testid={AiChatTestIds.AI_BOT_MESSAGE_CONTENT_LIST}
            sx={messageContentListStyles}
          >
            {effectiveMessageContent &&
              effectiveMessageContent
                .filter(isValidMessageContentItem)
                .map(item => (
                  <MessageContent
                    contentProps={item.contentProps}
                    contentType={item.contentType}
                    key={item.id}
                    onCopy={onCopy}
                  />
                ))}
            {!!formattedContent && (
              <Box>
                {formattedContent}
                {isStreaming && (
                  <Box
                    aria-hidden
                    component="span"
                    data-testid={AiChatTestIds.AI_BOT_MESSAGE_STREAMING_CARET}
                    sx={streamingCaretStyles}
                  />
                )}
              </Box>
            )}
          </Stack>

          <ToolConfirmationCard messageId={messageId} />

          <PendingQuestionCard messageId={messageId} />

          {!isStreaming && !_.isNil(messageId) && <FeedbackButtons messageId={messageId} />}

          {isShownTools && (
            <MessageTools
              className={MESSAGE_TOOLS_CLASS_NAME}
              sx={messageToolsHiddenStyles}
              timestamp={timestamp}
              onCopy={onCopyAll}
            />
          )}
        </Stack>
      )}
    </Box>
  );
};

BotMessage.propTypes = {
  message: PropTypes.shape({
    id: PropTypes.string,
    content: PropTypes.string,
    messageContent: PropTypes.arrayOf(
      PropTypes.shape({
        contentType: PropTypes.oneOf(Object.values(MessageContentType)).isRequired,
        contentProps: PropTypes.object.isRequired,
      }),
    ),
    messageStatus: PropTypes.shape({
      status: PropTypes.oneOf(Object.values(MessageStatusType)),
      errorsAmount: PropTypes.number,
      statusMessage: PropTypes.string,
    }),
    timestamp: PropTypes.string,
    status: PropTypes.string,
    isStreaming: PropTypes.bool,
    error: PropTypes.string,
    canRetry: PropTypes.bool,
    isCancelled: PropTypes.bool,
    relatedUserMessageId: PropTypes.string,
    userOutcome: PropTypes.string,
    hasExecutionPlan: PropTypes.bool,
  }),
  loanId: PropTypes.string,
  isShownTools: PropTypes.bool,
};

export default memo(BotMessage);
